<template>
    <header class="custom-navigation-bar full-width top-nav-bar" role="banner">
        <div class="top-nav-left">
            <button
                type="button"
                class="top-nav-icon-btn cursor-pointer"
                aria-label="Toggle sidebar"
                @click="emit('toggle-drawer')"
            >
                <v-icon :icon="menuIcon" size="20" />
            </button>
            <span
                class="top-nav-divider d-none d-sm-block"
                aria-hidden="true"
            ></span>
            <nav class="top-nav-breadcrumb" aria-label="Breadcrumb">
                <template v-if="projectSelected && projectName">
                    <button
                        type="button"
                        class="top-nav-crumb top-nav-crumb--muted cursor-pointer d-none d-sm-inline"
                        title="Click to change project"
                        @click="emit('change-project')"
                    >
                        {{ projectName }}
                    </button>
                    <v-icon
                        :icon="mdiChevronRight"
                        size="16"
                        class="top-nav-crumb-sep d-none d-sm-inline-flex"
                    />
                </template>
                <span class="top-nav-crumb top-nav-crumb--current">{{
                    pageTitle
                }}</span>
            </nav>
        </div>

        <div class="top-nav-actions">
            <button
                v-if="projectSelected"
                type="button"
                class="nav-search-trigger d-flex align-center ga-2 cursor-pointer"
                aria-label="Search modules"
                @click="emit('search')"
            >
                <v-icon
                    :icon="mdiMagnify"
                    size="18"
                    class="nav-search-icon"
                />
                <span class="nav-search-placeholder d-none d-sm-inline"
                    >Search modules...</span
                >
                <kbd class="nav-kbd ml-auto d-none d-md-inline-flex"
                    >Ctrl K</kbd
                >
            </button>

            <NotificationMenu />

            <button
                type="button"
                class="top-nav-icon-btn cursor-pointer d-none d-sm-inline-flex"
                :aria-label="
                    isDark ? 'Switch to light mode' : 'Switch to dark mode'
                "
                @click="emit('toggle-theme')"
            >
                <v-icon
                    :icon="isDark ? mdiWeatherSunny : mdiWeatherNight"
                    size="18"
                />
            </button>

            <button
                type="button"
                class="nav-project-chip d-none d-sm-flex align-center ga-2 cursor-pointer"
                :class="projectBadgeClass"
                :title="
                    projectSelected
                        ? 'Click to change project'
                        : 'Select a project'
                "
                @click="emit('change-project')"
            >
                <v-icon
                    :icon="projectTypeIcon"
                    size="13"
                    class="project-type-icon flex-shrink-0"
                />
                <template v-if="projectSelected">
                    <span class="nav-project-code">{{ projectCode }}</span>
                    <span class="nav-chip-sep d-none d-md-block">·</span>
                    <span class="nav-project-name d-none d-md-block">{{
                        projectName
                    }}</span>
                </template>
                <span v-else class="nav-project-name">Select Project</span>
            </button>
        </div>
    </header>
</template>

<script setup>
import {
    mdiWeatherSunny,
    mdiWeatherNight,
    mdiMenu,
    mdiChevronRight,
    mdiMagnify,
} from "@mdi/js";
import NotificationMenu from "@/components/Common/NotificationMenu.vue";

defineProps({
    projectSelected: {
        type: Boolean,
        default: false,
    },
    projectCode: {
        type: String,
        default: "",
    },
    projectName: {
        type: String,
        default: "",
    },
    projectBadgeClass: {
        type: String,
        default: "project-badge-default",
    },
    projectTypeIcon: {
        type: String,
        required: true,
    },
    isDark: {
        type: Boolean,
        default: false,
    },
    pageTitle: {
        type: String,
        default: "",
    },
    menuIcon: {
        type: String,
        default: mdiMenu,
    },
});

const emit = defineEmits([
    "search",
    "toggle-theme",
    "change-project",
    "toggle-drawer",
]);
</script>

<style lang="scss" scoped>
.top-nav-bar {
    top: 0;
    left: 0;
    right: 0;
    height: 56px;
    gap: 0.5rem;
    padding: 0 0.75rem;
    box-shadow: none;
    backdrop-filter: none;
    background: var(--background);
    border-bottom: none;

    @media (min-width: 600px) {
        gap: 1rem;
        padding: 0 1rem;
    }
}

.top-nav-left {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    min-width: 0;
    flex: 1;

    @media (min-width: 600px) {
        gap: 0.75rem;
    }
}

.top-nav-divider {
    flex-shrink: 0;
    width: 1px;
    height: 16px;
    background: var(--border);
}

.top-nav-breadcrumb {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    min-width: 0;
    overflow: hidden;
}

.top-nav-crumb {
    white-space: nowrap;
    font-size: 14px;
    line-height: 1.25;
    border: none;
    background: transparent;
    padding: 0;
    font-family: inherit;

    &--muted {
        color: var(--muted-foreground);
        font-weight: 400;
        flex-shrink: 0;
        transition: color 0.15s ease;

        &:hover {
            color: var(--foreground);
        }

        &:focus-visible {
            outline: 2px solid var(--ring);
            outline-offset: 2px;
            border-radius: 2px;
        }
    }

    &--current {
        overflow: hidden;
        text-overflow: ellipsis;
        color: var(--foreground);
        font-weight: 600;
        min-width: 0;
        flex-shrink: 1;
    }
}

.top-nav-crumb-sep {
    flex-shrink: 0;
    color: var(--muted-foreground);
}

.top-nav-actions {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    flex-shrink: 0;
    margin-left: auto;

    @media (min-width: 600px) {
        gap: 0.75rem;
    }
}

.top-nav-icon-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    border: 1px solid transparent;
    border-radius: var(--radius-sm);
    background: transparent;
    color: var(--muted-foreground);
    transition: color 0.15s ease, background-color 0.15s ease,
        border-color 0.15s ease;

    &:hover {
        color: var(--foreground);
        background: var(--muted);
        border-color: var(--border);
    }

    &:focus-visible {
        outline: 2px solid var(--ring);
        outline-offset: 2px;
    }
}

.nav-search-trigger {
    height: 34px;
    min-width: 34px;
    justify-content: center;
    padding: 0;
    border: 1px solid transparent;
    background: transparent;
    border-radius: var(--radius-sm);
    color: var(--muted-foreground);
    font-size: 13px;
    transition: border-color 0.15s ease, background-color 0.15s ease,
        color 0.15s ease;

    &:hover {
        color: var(--foreground);
        background: var(--muted);
        border-color: var(--border);
    }

    &:focus-visible {
        outline: 2px solid var(--ring);
        outline-offset: 2px;
    }

    @media (min-width: 600px) {
        min-width: 200px;
        justify-content: flex-start;
        padding: 0 0.75rem;
        border-color: var(--secondary);
        background: var(--secondary);

        &:hover {
            background: var(--input);
            color: var(--card-foreground);
            border-color: var(--secondary);
        }
    }
}

.nav-search-icon {
    flex-shrink: 0;
    color: inherit;
}

.nav-search-placeholder {
    font-size: 13px;
    color: var(--card-foreground);
}

.nav-kbd {
    display: inline-flex;
    align-items: center;
    padding: 0 0.375rem;
    border: 1px solid var(--border);
    border-radius: 4px;
    font-size: 11px;
    font-family: inherit;
    color: var(--muted-foreground);
    background: var(--background);
    white-space: nowrap;
}
</style>
